import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, X, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { alertsApi, Alert } from '@/lib/api'
import { cn } from '@/lib/utils'

export default function AlertBanner() {
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set())
  const [expandedAlerts, setExpandedAlerts] = useState<Set<string>>(new Set())

  const { data: alertsData } = useQuery({
    queryKey: ['alerts'],
    queryFn: alertsApi.getAlerts,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 2 * 60 * 1000, // Consider stale after 2 minutes
  })

  const alerts = alertsData?.alerts || []
  const visibleAlerts = alerts.filter(alert => !dismissedAlerts.has(alert.id))

  if (visibleAlerts.length === 0) {
    return null
  }

  const dismissAlert = (alertId: string) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]))
  }

  const toggleExpanded = (alertId: string) => {
    setExpandedAlerts(prev => {
      const newSet = new Set(prev)
      if (newSet.has(alertId)) {
        newSet.delete(alertId)
      } else {
        newSet.add(alertId)
      }
      return newSet
    })
  }

  const getAlertStyles = (alert: Alert) => {
    if (alert.type === 'error') {
      return 'bg-red-50 border-red-200 text-red-800'
    }
    if (alert.type === 'warning') {
      if (alert.severity === 'high') {
        return 'bg-orange-50 border-orange-200 text-orange-800'
      }
      return 'bg-yellow-50 border-yellow-200 text-yellow-800'
    }
    return 'bg-blue-50 border-blue-200 text-blue-800'
  }

  const getIconColor = (alert: Alert) => {
    if (alert.type === 'error') return 'text-red-500'
    if (alert.type === 'warning') {
      if (alert.severity === 'high') return 'text-orange-500'
      return 'text-yellow-500'
    }
    return 'text-blue-500'
  }

  return (
    <div className="space-y-2 mb-4">
      {visibleAlerts.map(alert => (
        <div
          key={alert.id}
          className={cn(
            'rounded-lg border p-4',
            getAlertStyles(alert)
          )}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className={cn('h-5 w-5 flex-shrink-0 mt-0.5', getIconColor(alert))} />

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-medium">{alert.title}</h3>
                <div className="flex items-center gap-2">
                  {alert.link && (
                    <Link
                      to={alert.link}
                      className="text-sm font-medium hover:underline flex items-center gap-1"
                    >
                      {alert.link_text || 'Ver'}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                  <button
                    onClick={() => dismissAlert(alert.id)}
                    className="p-1 rounded hover:bg-black/10 transition-colors"
                    title="Descartar alerta"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <p className="text-sm mt-1">{alert.message}</p>

              {alert.details && alert.details.length > 0 && (
                <div className="mt-2">
                  <button
                    onClick={() => toggleExpanded(alert.id)}
                    className="text-sm font-medium flex items-center gap-1 hover:underline"
                  >
                    {expandedAlerts.has(alert.id) ? (
                      <>
                        <ChevronUp className="h-4 w-4" />
                        Ocultar detalles
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        Ver detalles ({alert.details.length} pedidos)
                      </>
                    )}
                  </button>

                  {expandedAlerts.has(alert.id) && (
                    <div className="mt-2 rounded bg-white/50 p-3">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-1 px-2">Factura</th>
                            <th className="text-left py-1 px-2">Pedido</th>
                            <th className="text-left py-1 px-2">Fecha</th>
                            <th className="text-right py-1 px-2">Dias</th>
                            <th className="text-right py-1 px-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alert.details.map((detail, idx) => (
                            <tr key={idx} className="border-b last:border-b-0">
                              <td className="py-1 px-2 font-mono">{detail.serie}-{detail.codigo}</td>
                              <td className="py-1 px-2">
                                {detail.order_reference || `#${detail.order_id}`}
                              </td>
                              <td className="py-1 px-2">
                                {detail.fecha ? new Date(detail.fecha).toLocaleDateString('es-ES') : '-'}
                              </td>
                              <td className="py-1 px-2 text-right font-medium">
                                {detail.days_old}
                              </td>
                              <td className="py-1 px-2 text-right">
                                {detail.total.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
